"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TransitTimeClient = void 0;
const _ = require("lodash");
const lru_cache_1 = require("lru-cache");
const commons_logs_1 = require("@deliverr/commons-logs");
const core_auth_1 = require("@deliverr/core-auth");
const business_types_1 = require("@deliverr/business-types");
const AbstractTransitTimeClient_1 = require("./AbstractTransitTimeClient");
const DAY_MS = 24 * 60 * 60 * 1000;
const defaultConfig = {
    authEngine: core_auth_1.AuthEngine.BASIC,
    baseURL: process.env.TRANSIT_TIME_SERVICE_URL,
    basicAuthUser: process.env.TRANSIT_TIME_BASIC_AUTH_USER,
    basicAuthPassword: process.env.TRANSIT_TIME_BASIC_AUTH_PASSWORD,
    fromZipGroundToZipsCache: new lru_cache_1.LRUCache({
        max: 200,
        ttl: DAY_MS,
    }),
    fromZipGroundToZipsCacheForPrediction: new lru_cache_1.LRUCache({
        max: 200,
        ttl: DAY_MS,
    }),
};
class TransitTimeClient extends AbstractTransitTimeClient_1.AbstractTransitTimeClient {
    constructor(config = {}) {
        const clientConfig = Object.assign(Object.assign({}, defaultConfig), config);
        super(clientConfig);
        this.fromZipGroundToZipsCache = clientConfig.fromZipGroundToZipsCache;
        this.fromZipGroundToZipsCacheForPrediction = clientConfig.fromZipGroundToZipsCacheForPrediction;
    }
    async getGroundToZipsByFromZips(fromZip3s, days = 2, estimatedShipDate, otdLevel = business_types_1.OtdLevels.High, verbose = false) {
        const cacheKey = (zip) => [zip, days, estimatedShipDate].filter(Boolean).join("-");
        const [cached, missingFromZips] = this.getCachedAndMissingZips(cacheKey, fromZip3s, this.fromZipGroundToZipsCache);
        if (_.isEmpty(missingFromZips)) {
            if (verbose) {
                commons_logs_1.log.info({ fromZips: _.keys(cached), days, estimatedShipDate }, "Retrieved groundToZips from cache");
            }
            return cached;
        }
        else {
            const uncached = await this.get(`v1/transit/toZips/${days}`, {
                fromZips: missingFromZips,
                shipDate: estimatedShipDate,
                otdLevel,
            });
            _.toPairs(uncached).forEach(([fromZip, toZips]) => this.fromZipGroundToZipsCache.set(cacheKey(fromZip), toZips));
            if (verbose) {
                commons_logs_1.log.info({
                    cached: _.keys(cached),
                    uncached: missingFromZips,
                    days,
                    estimatedShipDate,
                }, "groundToZips cache status by missingFromZips");
            }
            return _.fromPairs(_.union(_.toPairs(cached), _.toPairs(uncached)));
        }
    }
    async getGroundToZipsByFromZipsForPrediction(predictionId, fromZip3s, days = 2, estimatedShipDate, otdLevel = business_types_1.OtdLevels.High, verbose = false) {
        const cacheKey = (zip) => [predictionId, zip, days, otdLevel, estimatedShipDate].filter(Boolean).join("-");
        const [cached, missingFromZips] = this.getCachedAndMissingZips(cacheKey, fromZip3s, this.fromZipGroundToZipsCacheForPrediction);
        if (_.isEmpty(missingFromZips)) {
            if (verbose) {
                commons_logs_1.log.info({ fromZips: _.keys(cached), days, estimatedShipDate }, "Retrieved groundToZips from cache");
            }
            return cached;
        }
        else {
            const uncached = await this.get(`v2/transit/toZips/${days}`, {
                fromZips: missingFromZips,
                shipDate: estimatedShipDate,
                otdLevel,
                predictionId,
            });
            _.toPairs(uncached.groundToZipsByFromZips).forEach(([fromZip, toZips]) => this.fromZipGroundToZipsCacheForPrediction.set(cacheKey(fromZip), toZips));
            if (verbose) {
                commons_logs_1.log.info({
                    cached: _.keys(cached),
                    uncached: missingFromZips,
                    days,
                    estimatedShipDate,
                    predictionId,
                }, "groundToZips cache status by missingFromZips");
            }
            return _.fromPairs(_.union(_.toPairs(cached), _.toPairs(uncached.groundToZipsByFromZips)));
        }
    }
    async getTransitTimeDays(fromZip, destZip, dates, includedShippingMethods, otdLevel = business_types_1.OtdLevels.High, includedHubIds) {
        return this.getCached("v2/delivery-days", {
            fromZip,
            destZip,
            includedShippingMethods,
            dates,
            otdLevel,
            includedHubIds,
        });
    }
    async getAllZips(zipDigits = 5, includeDasZips = false) {
        return this.getCached("v1/zips", { zipDigits, includeDasZips });
    }
    async expandZips(zips, zipDigits = 5, includeDasZips = false) {
        return this.post("v1/zips", zips, {
            zipDigits,
            includeDasZips,
        });
    }
    getCachedAndMissingZips(cacheKey, fromZips, lruCache) {
        const cached = _.fromPairs(fromZips.map((fromZip) => [fromZip, lruCache.get(cacheKey(fromZip))]).filter((pair) => pair[1]));
        const missingFromZips = _.difference(fromZips, _.keys(cached));
        return [cached, missingFromZips];
    }
}
exports.TransitTimeClient = TransitTimeClient;
//# sourceMappingURL=TransitTimeClient.js.map